import { Middleware } from '@reduxjs/toolkit';
import {
  AgentFirstCartEditVia,
  resolveFirstCartEditVia,
} from '../constants/agentFirstCartEdit';
import { EventTypes } from '../constants/event';
import { messagingActions } from '../reducers/messagingSlice';
import { selectIsShadowModeEnabled } from '../redux/features/config/config.selector';
import {
  resetSessionBoundaryData,
  setSessionStart,
} from '../redux/features/sessionBoundary/sessionBoundary.slice';
import { TaskStatuses } from '../redux/features/taskRouter/taskRouter.constants';
import { updateTaskStatus } from '../redux/features/taskRouter/taskRouter.slice';
import { RootStore } from './store';

/**
 * HITL-1646 — emit `agent_first_cart_edit` once per session, and control
 * nothing with it.
 *
 * ## Why a middleware, and not a hook at each call site
 *
 * There are seven ways to write into My Cart — Copy all, a per-item copy, an
 * omnibar add, a modifier or size change, a quantity step, a duplicate, a
 * delete — spread across the centre pane, the omnibar, the cart rows and two
 * thunks. A hook per call site is seven places to forget, and the first one
 * forgotten silently understates the number the feature is judged by. Every one
 * of them ends in a `draftCart/*` action, so one watcher here covers all seven
 * and covers the next one for free.
 *
 * ## Why the state lives in this closure
 *
 * `final_order_validation` was control-coupled and died in production; the
 * standing rule since is that new observability must control NOTHING. Both
 * pieces of state this needs — the one-shot latch and the task-loaded anchor —
 * live in this closure rather than in a slice, so there is no field for another
 * behaviour to start reading and no flag to get out of step. Nothing in the app
 * can observe whether this has fired.
 *
 * The anchor is taken here for the same reason: the task-loading metrics live in
 * a React context, not Redux, and `taskAssignedSessionStartedAt` is the
 * CUSTOMER's session start (the car-enter moment on the `task_assigned` event),
 * not the moment the task reached this agent's screen. Stamping it as the
 * assignment passes through is the honest measurement of the latency this metric
 * claims to report.
 */
const agentFirstCartEditMiddleware: Middleware = (store: RootStore) => {
  /** Armed at every session start; spent by the first edit. */
  let emitted = false;
  /** Wall clock at the moment the task reached this agent. */
  let taskLoadedAt: number | null = null;

  const startSession = () => {
    emitted = false;
    taskLoadedAt = Date.now();
  };

  const emit = (via: AgentFirstCartEditVia) => {
    emitted = true;
    const state = store.getState();
    store.dispatch(
      messagingActions.sendAgentMetric({
        data: {
          metric: 'agent_first_cart_edit',
          via,
          ms_since_task_loaded:
            taskLoadedAt === null ? null : Date.now() - taskLoadedAt,
          ai_status: state.ai?.aiStatus?.status ?? null,
        },
      })
    );
  };

  return (next) => (action) => {
    // Let the action land first. A metric describing an edit must not be able to
    // precede the edit it describes, and reading `ai.aiStatus` after `next`
    // reports the colour the agent was actually looking at.
    const result = next(action);

    const type = typeof action?.type === 'string' ? action.type : '';

    // Re-arm on every session start, by both routes. Same pair the draft cart
    // and the agent mode reset on, so the three cannot disagree about where a
    // session begins.
    if (
      type === setSessionStart.type ||
      type === resetSessionBoundaryData.type
    ) {
      startSession();
      return result;
    }

    // The task-router lane: stamp the anchor when the task is assigned to this
    // agent. This also re-arms — a new task is a new customer.
    if (type === updateTaskStatus.type) {
      const event = (action?.payload as { event?: string })?.event;
      if (event === TaskStatuses.assigned) startSession();
      return result;
    }

    // The single-restaurant lane, where a session begins on car_enter. The
    // anchor is left null there: nothing landed on the agent's screen, so there
    // is no latency to report and reporting one would be a fiction.
    if (type === messagingActions.messageReceived.type) {
      const payload = action?.payload as {
        messages?: { event?: string }[];
        isSessionBufferedMessages?: boolean;
      };
      // A buffered payload REPLAYS past events, so its car_enter is history.
      if (
        !payload?.isSessionBufferedMessages &&
        payload?.messages?.some(({ event }) => event === EventTypes.carEnter)
      ) {
        emitted = false;
        taskLoadedAt = null;
      }
      return result;
    }

    if (emitted) return result;

    const via = resolveFirstCartEditVia(action);
    if (!via) return result;

    // Gated on the feature's own flag and nothing else. No new flag: a second
    // switch in front of a metric is one more thing to be off when the data is
    // needed.
    if (!selectIsShadowModeEnabled(store.getState())) return result;

    emit(via);
    return result;
  };
};

export default agentFirstCartEditMiddleware;
